// =============================================================================
// Fase 5.5 — DocUploader singolo (usa docs registry per deduplicazione)
// =============================================================================
import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const MAX_MB = 20;
const ALLOWED_MIMES = [
  "application/pdf", "image/jpeg", "image/jpg", "image/png",
  "image/heic", "image/heif",
];
const ALLOWED_EXT = /\.(pdf|jpe?g|png|heic|heif)$/i;

export function DocUploader({
  docId,
  label,
  description,
  required = false,
  optional = false,
  currentDoc,   // { file_id, filename, status } | null
  onUploaded,   // (docId, {file_id, filename}) => void
  onCleared,    // (docId) => void
  testid,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const uploaded = !!(currentDoc && currentDoc.file_id && currentDoc.status !== "non_richiesto");

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    if (!ALLOWED_EXT.test(file.name) && !ALLOWED_MIMES.includes(file.type)) {
      setError("Formato non valido. Usa PDF, JPG, PNG o HEIC.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`File troppo grande (max ${MAX_MB} MB).`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", docId);
      const res = await axios.post(`${API}/convenzioni/upload-doc`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      onUploaded?.(docId, { file_id: res.data.file_id, filename: file.name });
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload fallito. Riprova.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isError = !!error;
  const state = isError ? "errore" : uploaded ? "caricato" : "mancante";

  return (
    <div
      className={`rounded-xl border-2 p-4 transition-all ${
        state === "caricato" ? "border-emerald-400 bg-emerald-50/50"
        : state === "errore" ? "border-red-400 bg-red-50/40"
        : required ? "border-[var(--brand-accent,#163321)]/40 bg-white"
        : "border-[#E5E2DB] bg-white"
      }`}
      data-testid={testid || `doc-uploader-${docId}`}
      data-doc-state={state}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#163321]" />
            : uploaded ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            : isError ? <XCircle className="w-5 h-5 text-red-600" />
            : <Upload className="w-5 h-5 text-[#4A5E51]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-[#0F1F14]">{label}</p>
            {required && !uploaded && (
              <span className="text-[10px] font-semibold text-red-700 uppercase tracking-wider">
                obbligatorio
              </span>
            )}
            {optional && (
              <span className="text-[10px] font-semibold text-[#7A8E83] uppercase tracking-wider">
                facoltativo
              </span>
            )}
            {uploaded && (
              <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">
                ✓ caricato
              </span>
            )}
          </div>
          {description && (
            <p className="text-[11px] text-[#4A5E51] mt-0.5">{description}</p>
          )}
          {uploaded && currentDoc?.filename && (
            <p className="text-[10px] text-emerald-800 mt-1 truncate" title={currentDoc.filename}>
              {currentDoc.filename}
            </p>
          )}
          {isError && (
            <p className="text-[11px] text-red-700 mt-1">{error}</p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <label
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--brand-accent,#163321)] text-white text-xs font-medium cursor-pointer hover:opacity-90 transition-opacity"
              data-testid={`${testid || `doc-uploader-${docId}`}-btn`}
            >
              {uploaded ? <RefreshCw className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
              {uploaded ? "Sostituisci" : "Carica file"}
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
            {uploaded && (
              <button
                type="button"
                onClick={() => onCleared?.(docId)}
                className="text-[11px] text-[#4A5E51] hover:text-red-700 underline"
                data-testid={`${testid || `doc-uploader-${docId}`}-clear`}
              >
                Rimuovi
              </button>
            )}
          </div>
          <p className="text-[10px] text-[#8B9A8F] mt-1.5">
            Formati: PDF, JPG, PNG, HEIC · Max {MAX_MB} MB
          </p>
        </div>
      </div>
    </div>
  );
}
